import { useCurrentUser as useStateUser } from '@centecomm/portal-service-react';
import PrivacyPolicyPage from 'Core/StaticPages/PrivacyPolicyPage';
import ProductDocumentationPage from 'Core/StaticPages/ProductDocumentationPage';
import TermsOfServicePage from 'Core/StaticPages/TermsOfServicePage';
import ConversationsPage from 'Features/Conversations/Pages/ConversationsPage';
import Dashboard from 'Features/Dashboard';
import NotificationsPage from 'Features/Notifications/Pages/NotificationsPage';
import TasksPage from 'Features/Tasks/Pages/TasksPage';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { LoadingContent, NoContent } from 'Shared/PageElements';
import { PageWrapper } from 'Shared/PageWrapper';
import { LeasingActions, LeasingActionTypes } from 'State/Leasing/actions';
import useSettingsFromState from 'State/Settings/Hooks/useSettingsFromState';
import { useOccupantsFromWelcomeState } from 'State/Welcome/Hooks/useOccupantsFromWelcomeState';
import { IsolatedLayout, StandardLayout } from '../Layouts';
import tenantApplications from '../RouteConfigurations/tenantApplications';
import tenantFeatures from '../RouteConfigurations/tenantFeatures';
import useFeatureApplications from '../RouteConfigurations/useFeatureApplications';
import useFeatureRoutes from '../RouteConfigurations/useFeatureRoutes';
import { FeatureApplication } from '../Types/FeatureApplication';

const AccountFeature = React.lazy(() => import('Features/Account/AccountFeature'));

function TenantRoot(): React.ReactElement {
    const { t } = useTranslation(['core']);
    const history = useHistory();
    const dispatch = useDispatch();

    const { isLoaded: isSettingsFullyLoaded, hasError: hasSettingsError, settings } = useSettingsFromState();
    const { hasActiveApplication, currentUser } = useStateUser();
    const { featureApplications } = useFeatureApplications(tenantApplications);
    const { navigationLinks, featureRoutes } = useFeatureRoutes(tenantFeatures);
    const { areLoaded: isOccupantsDataLoaded, hasOccupantToSetup, welcomeFlowDone } = useOccupantsFromWelcomeState();

    const [applicationsResolved, toggleApplicationsResolved] = useState<boolean>(false);
    const settingsReady = isSettingsFullyLoaded && !hasSettingsError;
    const welcomeFlowEnabled = settingsReady && settings?.welcome?.visible;

    useEffect(() => {
        const setActiveApplication = async () => {
            let allowedToStartLeaseApplication = true;

            if (welcomeFlowEnabled && hasOccupantToSetup) {
                const welcomeFeature: FeatureApplication | undefined = featureApplications.find(
                    (app) => app.url === '/app/welcome',
                );

                if (welcomeFeature) {
                    allowedToStartLeaseApplication = false;
                    toggleApplicationsResolved(true);
                    history.push(welcomeFeature.url);
                    return;
                }
            }

            if (hasActiveApplication && allowedToStartLeaseApplication) {
                const leasingFeature: FeatureApplication | undefined = featureApplications.find(
                    (app) => app.url === '/app/lease-application',
                );

                if (currentUser?.tenant?.activeLeaseApplication) {
                    dispatch({
                        type: LeasingActions.SET_LOAD_APPLICATION,
                        payload: {
                            id: currentUser.tenant.activeLeaseApplication.id,
                            started: currentUser.tenant.activeLeaseApplication.hasStartedApplication,
                            creditReportType: currentUser.tenant.activeLeaseApplication.creditReportType,
                            creditReportEnabled: currentUser.tenant.activeLeaseApplication.creditReportEnabled,
                        },
                    } as LeasingActionTypes);
                } else {
                    dispatch({
                        type: LeasingActions.SET_LOAD_APPLICATION,
                    } as LeasingActionTypes);
                }

                if (leasingFeature) {
                    toggleApplicationsResolved(true);
                    history.push(leasingFeature.url);
                }
            }

            toggleApplicationsResolved(true);
        };
        if (settingsReady && isOccupantsDataLoaded) setActiveApplication();
    }, [settingsReady, welcomeFlowEnabled, isOccupantsDataLoaded, welcomeFlowDone]);

    if (!applicationsResolved) return <LoadingContent />;

    return (
        <Switch>
            <Route path="/app">
                <Switch>
                    {featureApplications.map(({ url, PageRoot, LeftHeaderComponent, RightHeaderComponent }) => (
                        <Route path={url} key={`${url.toLowerCase().replace(' ', '-')}`}>
                            <IsolatedLayout
                                LeftHeaderComponent={LeftHeaderComponent}
                                RightHeaderComponent={RightHeaderComponent}
                            >
                                {PageRoot}
                            </IsolatedLayout>
                        </Route>
                    ))}
                    <Redirect strict from="/app" to={featureApplications.length > 0 ? featureApplications[0].url : '/'} />
                </Switch>
            </Route>
            <Route path="/">
                <StandardLayout navigationLinks={navigationLinks}>
                    <Switch>
                        <Route exact path="/">
                            <Dashboard.Page />
                        </Route>
                        <Route strict path="/account">
                            <React.Suspense fallback={<LoadingContent />}>
                                <AccountFeature />
                            </React.Suspense>
                        </Route>
                        <Route strict path="/notifications">
                            <NotificationsPage />
                        </Route>
                        <Route strict path="/tasks">
                            <TasksPage />
                        </Route>
                        <Route strict path="/conversations">
                            <ConversationsPage />
                        </Route>
                        <Route strict path="/terms-of-service">
                            <TermsOfServicePage />
                        </Route>
                        <Route strict path="/privacy-policy">
                            <PrivacyPolicyPage />
                        </Route>
                        {!hasSettingsError && isSettingsFullyLoaded && settings?.helpGuides?.visible && (
                            <Route strict path="/product-documentation">
                                <ProductDocumentationPage />
                            </Route>
                        )}
                        {featureRoutes.map(
                            ({ url, text, tenantRoot }) =>
                                tenantRoot && (
                                    <Route path={`${url}`} key={`main-${text.toLowerCase().replace(' ', '-')}`}>
                                        {tenantRoot}
                                    </Route>
                                ),
                        )}
                        <Route path="*">
                            <PageWrapper pageTitle={t('core:notFound.title')}>
                                <NoContent message={t('core:notFound.subtitle')} />
                            </PageWrapper>
                        </Route>
                    </Switch>
                </StandardLayout>
            </Route>
        </Switch>
    );
}

export default TenantRoot;
